import { Box, Typography, Divider } from "@mui/material";
import { useEffect, useState, useRef, useMemo } from "react";
import { Paper } from "@mui/material";
import { useAuth } from "context/authContext";
import { getMyTrainingsPageData } from "services/api";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { useLocation } from "react-router-dom";
import LoadingScreen from "components/Loading";
import {
  GapAnalysisChart,
  ProgressChart,
} from "layouts/user/Competencies/learningAndDevelopment/components/Charts";
import TrainingCard from "layouts/user/Competencies/learningAndDevelopment/components/TrainingsCard";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

export default function Trainings() {
  const { t } = useTranslation();
  const [loadingTeamData, setLoadingTeamData] = useState(true);
  const { employee, selectedMonth, setSelectedMonth, selectedYear, setSelectedYear } = useAuth();
  const [selectedEmpTraining, setSelectedEmpTraining] = useState([]);
  const snackbarRef = useRef();
  const location = useLocation();
  const employeeId = location.state?.employeeId;
  const employeeName = location.state?.employeeName;
  const [myTrainingsData, setMyTrainingsData] = useState(null);
  const [showEnded, setShowEnded] = useState(false);

  useEffect(() => {
    const idToLoad = employeeId !== undefined ? employeeId : employee.id;
    console.log("idToLoad", idToLoad);
    const fetchTrainingData = async () => {
      setLoadingTeamData(true);
      try {
        const res = await getMyTrainingsPageData(idToLoad, selectedMonth, selectedYear);
        console.log("Res::",res);
        // --- SORTING LOGIC START ---
        if (res && Array.isArray(res.trainingDetails)) {
          res.trainingDetails.sort((a, b) => {
            const dateA = new Date(a[3]); // End Date is at index 3
            const dateB = new Date(b[3]);

            // Sort descending: Furthest/Latest date first (b - a)
            return dateB.getTime() - dateA.getTime();
          });
        }
        // --- SORTING LOGIC END ---
        console.log("My Trainings Data::", res);
        setMyTrainingsData(res);
      } catch (e) {
        snackbarRef.current?.showSnackbar(t("user.trainings.unexpectedError"), "error");
      } finally {
        setLoadingTeamData(false);
      }
    };
    if (idToLoad) {
      fetchTrainingData();
    }
  }, [employee, employeeId, selectedMonth, selectedYear]);

  const chartData = useMemo(() => {
    // If data hasn't loaded yet or is empty, return empty arrays
    if (!myTrainingsData?.competencyAssessments) {
      return { labels: [], gapData: [] };
    }

    // Transform the raw API data into what the chart needs
    const labels = myTrainingsData.competencyAssessments.map((item) => item.competencyName);

    const gapData = myTrainingsData.competencyAssessments.map((item) => {
      const actual = item.actualCompLevelValue ?? 0;
      const target = item.targetCompLevelValue ?? 0;
      // GAP calculation: e.g., 3 - 3 = 0, or 1 - 3 = -2
      return actual - target;
    });

    return { labels, gapData };
  }, [myTrainingsData]); // This ONLY recalculates when myTrainingsData changes

  // const { totalTrainings, finishedTrainings } = useMemo(() => {
  //   // 1. Get the array from your state
  //   const trainings = myTrainingsData?.trainingDetails || [];

  //   // 2. Count the total
  //   const total = trainings.length;

  //   // 3. Filter for those where the End Date (Index 3) is in the past
  //   const finished = trainings.filter((training) => {
  //     const endDateValue = training[3];

  //     if (!endDateValue) return false;

  //     const endDate = new Date(endDateValue);
  //     const today = new Date();

  //     return endDate <= today;
  //   }).length;

  //   return {
  //     totalTrainings: total,
  //     finishedTrainings: finished,
  //   };
  // }, [myTrainingsData]); // This updates whenever your API call finishes and sets state

  const { activeTrainings, endedTrainings, totalTrainings, finishedTrainings } = useMemo(() => {
    // 1. Get the array from your state
    const trainings = myTrainingsData?.trainingDetails || [];
    const now = new Date();

    const active = [];
    const ended = [];

    // We use a single loop to separate the lists and count them at the same time
    trainings.forEach((row) => {
      const endDate = new Date(row[3]);

      if (endDate >= now) {
        active.push(row);
      } else {
        ended.push(row);
      }
    });

    return {
      activeTrainings: active,
      endedTrainings: ended,
      totalTrainings: trainings.length,
      finishedTrainings: ended.length, // This is exactly the count of past trainings
    };
  }, [myTrainingsData]);

  if (loadingTeamData) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ width: "100%", overflowX: "auto", mt: 1, mb: 3 }}>
      <Paper
        sx={{
          p: 2,
          borderRadius: "12px",
          background: "#ffffff",
          boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
          border: "1px solid #f1f5f9",
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            width: "100%",
            height: "100%",
          }}
        >
          {/* LEFT HALF — GAP ANALYSIS */}
          <GapAnalysisChart
            labels={chartData.labels}
            gapData={chartData.gapData}
            employeeName={employeeName}
            t={t}
          />

          {/* DIVIDER */}
          <Box
            sx={{
              width: { xs: "100%", md: "1px" },
              background: "#d1d5db", // darker divider
              height: { xs: 1, md: "auto" },
            }}
          />
          {/* RIGHT HALF — L&D PROGRESS */}
          <ProgressChart
            finishedTrainings={finishedTrainings}
            totalTrainings={totalTrainings}
            t={t}
          />
        </Box>
      </Paper>
      <Divider sx={{ my: 1, ml: 1, height: 2, backgroundColor: "#9ca3af" }} />

      <Typography variant="body2" fontWeight="bold" sx={{ ml: 1, color: "#1f2937" }}>
        {t("user.competencies.myLandD")}
      </Typography>

      <Divider sx={{ my: 1, ml: 1, height: 2, backgroundColor: "#9ca3af" }} />

      <Box display="flex" flexDirection="column" gap={activeTrainings.length > 0 ? 1 : 0}>
        {/* Map through trainingDetails from your new state object */}
        {activeTrainings.map((row, index) => (
          <TrainingCard
            key={index}
            row={row} // This is the [38, "Financial Auditing Course", ...] array
            index={index}
            t={t}
            empId={employeeId} // Using the employeeId from your logic
            // Data Props (Sending empty fallbacks if you don't have these yet)
            empTrainingOptions={[]}
            trainingCompMap={{}}
            jobLvls={[]}
            empCompGaps={[]}
            selectedLevelIds={{}}
            // State Props
            isEditing={false} // Default to not editing
            savingRowIndex={null}
            // Dummy Functions (Empty functions to prevent errors)
            toggleEditing={() => {}}
            handleSaveTrainingRow={() => {}}
            handleTrainingFieldChange={() => {}}
            handleAchievedLevelChange={() => {}}
            setRowToDelete={() => {}}
            setConfOpen={() => {}}
            isMyTrainingsView={true}
          />
        ))}

        {endedTrainings.length > 0 && (
          <Button
            onClick={() => setShowEnded(!showEnded)}
            startIcon={showEnded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            variant="text"
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              color: "#64748b", // Matching your Blue progress bar
            }}
          >
            {showEnded ? "Hide Ended Trainings" : `Show Ended Trainings (${finishedTrainings})`}
          </Button>
        )}

        {/* 3. ENDED LIST: Only adds space via 'gap' if button is visible above it */}
        {showEnded &&
          endedTrainings.map((row, index) => (
            <TrainingCard
              key={index}
              row={row} // This is the [38, "Financial Auditing Course", ...] array
              index={index}
              t={t}
              empId={employeeId} // Using the employeeId from your logic
              // Data Props (Sending empty fallbacks if you don't have these yet)
              empTrainingOptions={[]}
              trainingCompMap={{}}
              jobLvls={[]}
              empCompGaps={[]}
              selectedLevelIds={{}}
              // State Props
              isEditing={false} // Default to not editing
              savingRowIndex={null}
              // Dummy Functions (Empty functions to prevent errors)
              toggleEditing={() => {}}
              handleSaveTrainingRow={() => {}}
              handleTrainingFieldChange={() => {}}
              handleAchievedLevelChange={() => {}}
              setRowToDelete={() => {}}
              setConfOpen={() => {}}
              isMyTrainingsView={true}
            />
          ))}
      </Box>
    </Box>
  );
}
